import useFormatter from "@/hooks/use-formatter.hook.ts";
import { EmployeeViewModal } from "@/pages/employees/widgets/EmployeeViewModal.tsx";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { ApiEmployeeShort } from "@/types/employees/employee.types.ts";
import { useModals } from "@skailer-com/uikit";
import { useEffect } from "react";
import { useEffectOnce } from "react-use";

export function useMentionsHook() {
  const { renderEmployeeName } = useFormatter();

  const useMentionClickHandler = () => {
    const { openModal } = useModals();

    useEffectOnce(() => {
      const handleClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const mentionElement = target.closest('[data-type="mention"]');
        const isHTMLElement = mentionElement instanceof HTMLElement;

        if (!isHTMLElement) return;

        const id = Number(mentionElement.getAttribute("data-id"));

        if (!id) return;

        e.stopPropagation();
        e.preventDefault();

        openModal(EmployeeViewModal, {
          employeeId: id,
        });
      };

      document.addEventListener("click", handleClick, true);

      return () => {
        document.removeEventListener("click", handleClick, true);
      };
    });
  };

  const useMentionStyles = () => {
    const { data: employee } = useEmployeeMe();

    useEffect(() => {
      const userId = employee?.id;

      if (!userId) return;

      const styleTag = document.createElement("style");

      styleTag.textContent = `
        span[data-type="mention"][data-id="${userId}"] {      
          background-color: color-mix(in srgb, var(--color-warning) 5%, transparent);
          color: var(--color-warning);
        }
        span[data-type="mention"][data-id="${userId}"]:hover {
          background-color: color-mix(in srgb, var(--color-warning) 10%, transparent);
        }
      `;

      document.head.appendChild(styleTag);

      return () => styleTag.remove();
    }, [employee]);
  };

  function replaceMentions(text?: string, users?: ApiEmployeeShort[]): string {
    if (!text) return "";
    if (!users) return text;

    const mentionRegex =
      /<span data-type="mention" data-id="(\d+)" data-label="[^"]*">@[^<]*<\/span>/g;

    return text.replace(mentionRegex, (match) => {
      const idMatch = match.match(/data-id="(\d+)"/);
      if (!idMatch) return match;

      const userId = parseInt(idMatch[1]);

      const user = users.find((u) => u.id === userId);
      if (!user) return match;

      return `<span data-type="${user.is_archived ? "" : "mention"}" data-id="${userId}" data-label="${renderEmployeeName(user)}">@${renderEmployeeName(user)}</span>`;
    });
  }

  return { useMentionClickHandler, useMentionStyles, replaceMentions };
}
